import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Toolbar,
  Avatar,
  Stack,
  Chip,
  Alert,
  Button,
  Pagination,
  Card,
  CardContent,
  TextField,
  IconButton,
  Collapse,
  Skeleton,
  MenuItem,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { userSelector } from "../../user-redux/selector";
import { EventNoteOutlined, Email, Phone, LocationOn, Work, Description, Person, Search, Close, Download, CheckCircle, AttachMoney } from "@mui/icons-material";
import Key from "../../../clientVariables.json";
import axios from "axios";

const JobFairCandidates = () => {
  const dispatch = useDispatch();
  const { user } = useSelector(userSelector);
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const itemsPerPage = 10;
  const [candidateDetails, setCandidateDetails] = useState(null);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [showAllSkills, setShowAllSkills] = useState(false);
  const [experienceFilter, setExperienceFilter] = useState("all")

  useEffect(() => {
    if (Key?.domain_jobfair) {
      fetchJobFairCandidates();
    } else {
      setError("API endpoint not configured");
      setLoading(false);
    }
  }, []);

  const fetchJobFairCandidates = async () => {
    try {
      setLoading(true);
      setError(null);
      //console.log("Fetching from URL:", Key.domain_jobfair);

      const response = await fetch(Key.domain_jobfair, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      //console.log("API Response:", result);

      // Handle the API response structure: { success: 1, data: [{ users: [...] }] }
      let candidatesData = [];

      if (result.success === 1 && result.data && Array.isArray(result.data) && result.data.length > 0) {
        // Extract users array from the first data object
        if (result.data[0].users && Array.isArray(result.data[0].users)) {
          candidatesData = result.data[0].users;
        } else if (result.data[0] && Array.isArray(result.data[0])) {
          // Fallback: if data[0] is directly an array
          candidatesData = result.data[0];
        }
      } else if (Array.isArray(result.data)) {
        // Fallback: if data is directly an array
        candidatesData = result.data;
      } else if (Array.isArray(result)) {
        // Fallback: if response is directly an array
        candidatesData = result;
      }

      //console.log("Extracted candidates:", candidatesData.length);

      setCandidates(candidatesData);

      // Auto-select first candidate if available
      if (candidatesData.length > 0) {
        console.log("candidatesData[0]", candidatesData[0])
        setSelectedCandidate(candidatesData[0]);
        setCurrentPage(1); // Reset to first page
      }
    } catch (err) {
      console.error("Error fetching jobfair candidates:", err);
      console.error("Error details:", {
        message: err.message,
        stack: err.stack,
        url: Key.domain_jobfair,
      });

      let errorMessage = "Failed to fetch jobfair candidates. ";
      if (err.message) {
        errorMessage += err.message;
      } else {
        errorMessage += "Please check your connection and try again.";
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCandidateSelect = (candidate) => {
    setSelectedCandidate(candidate);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Filter candidates based on search query
  // const filteredCandidates = React.useMemo(() => {
  //   if (!searchQuery.trim()) {
  //     return candidates;
  //   }
  //   const query = searchQuery.toLowerCase().trim();
  //   return candidates.filter((candidate) => {
  //     const name = (candidate?.full_name || candidate?.name || candidate?.first_name || "").toLowerCase();
  //     const email = (candidate?.email || candidate?.email_address || "").toLowerCase();
  //     const role = (candidate?.role || candidate?.title || candidate?.job_title || "").toLowerCase();
  //     return name.includes(query) || email.includes(query) || role.includes(query);
  //   });
  // }, [candidates, searchQuery]);

  const filteredCandidates = React.useMemo(() => {
    let list = candidates;
    const quary = searchQuery.toLocaleLowerCase().trim()
    if (quary) {
      list = list.filter((candidate) => {
        const name = (candidate?.full_name || candidate?.name || candidate?.first_name || "").toLowerCase();
        const email = (candidate?.email || candidate?.email_address || "").toLowerCase();
        const role = (candidate?.role || candidate?.title || candidate?.job_title || "").toLowerCase();
        return (
          name.includes(quary) ||
          email.includes(quary) ||
          role.includes(quary)
        )
      })
    }
    if (experienceFilter !== "all") {
      list = list.filter((candidate) => {
        const rawExp = candidate?.total_experience ?? candidate?.experience ?? candidate?.exp ?? null
        if (rawExp === null || rawExp === undefined) return false;
        const exp = parseFloat(rawExp)
        if (isNaN(exp)) return false;
        switch (experienceFilter) {
          case "0-2":
            return exp < 3;
          case "3-5":
            return exp >= 3 && exp < 6;
          case "6+":
            return exp >= 6;
          default: return true
        }
      })
    }
    return list;
  }, [candidates, searchQuery, experienceFilter]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredCandidates.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCandidates = filteredCandidates.slice(startIndex, endIndex);

  // Reset to first page when search query changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, experienceFilter]);

  const getInitials = (name) => {
    try {
      if (!name || typeof name !== 'string') return "?";
      const trimmedName = name.trim();
      if (!trimmedName) return "?";
      const names = trimmedName.split(" ").filter(n => n.length > 0);
      if (names.length >= 2) {
        return `${names[0][0]}${names[1][0]}`.toUpperCase();
      }
      return trimmedName[0].toUpperCase();
    } catch (err) {
      console.error("Error getting initials:", err);
      return "?";
    }
  };

  const getAvatarColor = (name) => {
    try {
      const colors = [
        "#2196F3",
        "#4CAF50",
        "#FFC107",
        "#E91E63",
        "#673AB7",
        "#FF5722",
        "#009688",
        "#795548",
      ];
      if (!name || typeof name !== 'string') return colors[0];
      const index = name.charCodeAt(0) % colors.length;
      return colors[index];
    } catch (err) {
      console.error("Error getting avatar color:", err);
      return "#2196F3";
    }
  };

  const openResume = () => {
    const fileUrl = `${Key.domain_crm}/common/get_jobfair_file/${selectedCandidate.id}/${selectedCandidate.profile}`;
    const encodedUrl = encodeURIComponent(fileUrl);

    const viewerUrl = `https://docs.google.com/gview?url=${encodedUrl}&embedded=true`;

    window.open(viewerUrl, "_blank", "noopener,noreferrer");
  };


  const fetchCandidateDetailsByEmail = async (email) => {
    // Early validation
    if (!selectedCandidate?.resumeid) {
      setError("No resume ID selected");
      return;
    }

    setError(null);
    setCandidateDetails(null);
    setIsDetailsLoading(true);

    try {
      const response = await axios.post(
        "https://api.jobsnprofiles.com/resume/get_js_resume_details_by_email",
        {
          resumeid: selectedCandidate.resumeid,
          session_token: user?.session_token || "" // Add null check for user
        },
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      const result = response.data;
      console.log("Candidate details API response:", result); // Add logging

      if (result.success === 1) {
        setCandidateDetails(result.data);
      } else {
        setError(result.message || result.data || "No details found");
      }
    } catch (err) {
      console.error("Error fetching candidate details:", err);
      setError(err.response?.data?.message || err.message || "Failed to fetch candidate details");
    } finally {
      setIsDetailsLoading(false);
    }
  };
  // Auto-load details whenever selectedCandidate changes
  React.useEffect(() => {
    if (selectedCandidate && (selectedCandidate.email || selectedCandidate.email_address)) {
      fetchCandidateDetailsByEmail(selectedCandidate.email || selectedCandidate.email_address);
    } else {
      setCandidateDetails(null);
    }
    setShowAllSkills(false);
    // eslint-disable-next-line
  }, [selectedCandidate]);

  const normalizeEmail = (email) => (email || '').toString().trim().toLowerCase();

  // Always render something, even if there's an error
  if (loading) {
    return (
      <Box
        sx={{
          //p: 2,
          width: "100%",
          height: "calc(100vh - 64px)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            height: "100%",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Toolbar sx={{ minHeight: "64px !important", bgcolor: "#f8fafc", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
            <Skeleton variant="circular" width={28} height={28} sx={{ mr: 2 }} />
            <Skeleton variant="text" width={220} height={36} />
            <Box sx={{ flexGrow: 1 }} />
            <Skeleton variant="rounded" width={140} height={36} />
          </Toolbar>

          <Grid container spacing={0} sx={{ flexGrow: 1, minHeight: 0, overflow: "hidden", bgcolor: "#f5f5f5" }}>
            <Grid item xs={12} md={3} sx={{ height: "100%", minHeight: 0, pr: 0 }}>
              <Paper elevation={0} sx={{ height: "100%", borderRight: "1px solid rgba(0,0,0,0.12)", p: 1.5 }}>
                <Stack spacing={1.5}>
                  {Array.from({ length: 8 }).map((_, idx) => (
                    <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1 }}>
                      <Skeleton variant="circular" width={40} height={40} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="75%" height={20} />
                        <Skeleton variant="text" width="55%" height={16} />
                      </Box>
                    </Box>
                  ))}
                </Stack>
                <Box sx={{ mt: 2, borderTop: "1px solid rgba(0,0,0,0.08)", pt: 1.5, display: "flex", justifyContent: "center" }}>
                  <Skeleton variant="rounded" width={180} height={28} />
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={9} sx={{ height: "100%", minHeight: 0, pl: 0 }}>
              <Paper elevation={0} sx={{ height: "100%", ml: 2, p: 2 }}>
                <Stack spacing={2}>
                  <Skeleton variant="rounded" width="100%" height={120} />
                  <Skeleton variant="rounded" width="100%" height={140} />
                  <Skeleton variant="rounded" width="100%" height={140} />
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    );
  }

  // Error state - still show the UI
  if (error && candidates.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper elevation={0} sx={{ p: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
            <Button size="small" onClick={fetchJobFairCandidates} sx={{ ml: 2 }}>
              Retry
            </Button>
          </Alert>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        //p: 2,
        width: "100%",
        height: "calc(100vh - 64px)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box>
          <Toolbar sx={{ minHeight: "64px !important", bgcolor: "#f8fafc", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
            <EventNoteOutlined sx={{ fontSize: 28, color: "#2196f3", mr: 2 }} />
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
              JobFair Candidates
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Stack direction="row" spacing={2} alignItems="center">
              <Collapse in={searchExpanded} orientation="horizontal">
                <Box sx={{ display: "flex", alignItems: "center", minWidth: searchExpanded ? 300 : 0 }}>
                  <TextField
                    placeholder="Search by name, email, or role..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    size="small"
                    autoFocus={searchExpanded}
                    InputProps={{
                      startAdornment: (
                        <Search sx={{ color: "text.secondary", mr: 1, fontSize: 20 }} />
                      ),
                      endAdornment: searchQuery && (
                        <IconButton
                          size="small"
                          onClick={() => setSearchQuery("")}
                          sx={{ p: 0.5 }}
                        >
                          <Close sx={{ fontSize: 18 }} />
                        </IconButton>
                      ),
                    }}
                    sx={{
                      bgcolor: "#fff",
                      width: 300,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        "&:hover fieldset": {
                          borderColor: "#2196f3",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#2196f3",
                        },
                      },
                    }}
                  />
                </Box>
              </Collapse>

              <IconButton
                onClick={() => {
                  setSearchExpanded(!searchExpanded);
                  if (searchExpanded) {
                    setSearchQuery("");
                  }
                }}
                sx={{
                  bgcolor: searchExpanded ? "#2196f3" : "transparent",
                  color: searchExpanded ? "#fff" : "#666",
                  "&:hover": {
                    bgcolor: searchExpanded ? "#1976d2" : "rgba(33, 150, 243, 0.1)",
                  },
                }}
              >
                {searchExpanded ? <Close /> : <Search />}
              </IconButton>

              {/* 👇 NEW: Experience filter dropdown */}
              <TextField
                select
                size="small"
                label="Experience"
                value={experienceFilter}
                onChange={(e) => setExperienceFilter(e.target.value)}
                sx={{
                  minWidth: 150,
                  bgcolor: "#fff",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: "#10b981",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#10b981",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="0-2">0–2 years</MenuItem>
                <MenuItem value="3-5">3–5 years</MenuItem>
                <MenuItem value="6+">6+ years</MenuItem>
              </TextField>

              <Typography variant="body2" color="text.secondary">
                {filteredCandidates.length}{" "}
                {filteredCandidates.length === 1 ? "result" : "results"}
              </Typography>
            </Stack>

          </Toolbar>
        </Box>

        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
            <Button size="small" onClick={fetchJobFairCandidates} sx={{ ml: 2 }}>
              Retry
            </Button>
          </Alert>
        )}

        <Grid
          container
          spacing={0}
          sx={{
            backgroundColor: "#f5f5f5",
            flexGrow: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {/* Left Side - Candidates List */}
          <Grid item xs={12} md={3} sx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column", pr: 0 }}>
            <Paper elevation={0} sx={{ backgroundColor: "#fff", height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", borderRight: "1px solid rgba(0,0,0,0.12)" }}>
              {/* <Toolbar sx={{ minHeight: "48px !important", position: "sticky", top: 0, bgcolor: "#fff", zIndex: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Candidates ({candidates.length})
                </Typography>
              </Toolbar> */}
              <Divider />
              <Box sx={{ flex: 1, overflow: "auto" }}>
                <List sx={{ p: 0 }}>
                  {filteredCandidates.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        {searchQuery ? "No candidates match your search" : "No candidates found"}
                      </Typography>
                    </Box>
                  ) : (
                    currentCandidates.map((candidate, index) => {
                      if (!candidate) return null;
                      return (
                        <React.Fragment key={candidate.id || `candidate-${startIndex + index}`}>
                          <ListItemButton
                            selected={selectedCandidate === candidate}
                            onClick={() => handleCandidateSelect(candidate)}
                            sx={{
                              py: 1,
                              px: 1,
                              "&.Mui-selected": {
                                bgcolor: "rgba(33, 150, 243, 0.08)",
                                borderLeft: "3px solid #2196f3",
                                "&:hover": {
                                  bgcolor: "rgba(33, 150, 243, 0.12)",
                                },
                              },
                            }}
                          >
                            <Avatar
                              sx={{
                                bgcolor: getAvatarColor(candidate?.full_name || ""),
                                mr: 2,
                                width: 40,
                                height: 40,
                                fontSize: "15px",
                              }}
                            >
                              {getInitials(candidate?.full_name || "?")}
                            </Avatar>
                            <ListItemText
                              primary={
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                  {candidate?.full_name || candidate?.name || candidate?.first_name || "Unknown"}
                                </Typography>
                              }
                              secondary={
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 0.5 }}>
                                  {(candidate?.email || candidate?.email_address) && (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                      <Email sx={{ fontSize: 14, color: "text.secondary" }} />
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                                        {candidate?.email || candidate?.email_address}
                                      </Typography>
                                    </Box>
                                  )}

                                  {candidate?.role && (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                      <Work sx={{ fontSize: 14, color: "text.secondary" }} />
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                                        {candidate.role}
                                      </Typography>
                                    </Box>
                                  )}

                                  {candidate?.created && (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                      <EventNoteOutlined sx={{ fontSize: 14, color: "text.secondary" }} />
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                                        {new Date(candidate.created).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              }
                            />
                          </ListItemButton>
                          {index < currentCandidates.length - 1 && <Divider />}
                        </React.Fragment>
                      );
                    })
                  )}
                </List>
              </Box>
              {/* Pagination */}
              {filteredCandidates.length > itemsPerPage && (
                <Box sx={{ p: 2, borderTop: "1px solid rgba(0,0,0,0.08)", display: "flex", justifyContent: "center" }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="small"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Right Side - Candidate Details */}
          <Grid item xs={12} md={9} sx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column", pl: 0 }}>
            <Paper elevation={0} sx={{ backgroundColor: "#fff", height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", p: 0, ml: 0.5 }}>
              {selectedCandidate ? (
                <Box sx={{ height: "100%", position: "relative" }}>
                  <Box sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    {/* Header Section */}
                    <Box sx={{
                      bgcolor: "#fff",
                      background: "#fff",
                      p: { xs: 1.5, md: 2 },
                      borderBottom: "1px solid rgba(0,0,0,0.08)",
                      position: "relative",
                      overflow: "hidden",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    }}>
                      <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between" sx={{ position: "relative", zIndex: 1 }}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                          <Box sx={{ position: "relative" }}>
                            <Avatar
                              sx={{
                                bgcolor: "#667eea",
                                color: "#fff",
                                width: { xs: 70, md: 90 },
                                height: { xs: 70, md: 90 },
                                fontSize: { xs: 22, md: 25 },
                                border: "4px solid #f0f0f0",
                                boxShadow: "0 8px 24px rgba(102, 126, 234, 0.25)",
                                fontWeight: 700,
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  transform: "scale(1.05)",
                                  boxShadow: "0 12px 32px rgba(102, 126, 234, 0.35)",
                                }
                              }}
                            >
                              {getInitials(selectedCandidate.full_name || selectedCandidate.name || selectedCandidate.first_name || "?")}
                            </Avatar>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="h4"
                              sx={{
                                fontWeight: 800,
                                mb: 1.5,
                                color: "#1a202c",
                                fontSize: { xs: "1.5rem", md: "1.5rem" },
                                letterSpacing: "-0.5px"
                              }}
                            >
                              {(() => {
                                const selectedEmail = normalizeEmail(selectedCandidate?.email || selectedCandidate?.email_address);
                                const detailsEmail = normalizeEmail(candidateDetails?.resumedetails?.email_address || candidateDetails?.resumedetails?.email);
                                const isDetailsForCurrent = selectedEmail && detailsEmail && selectedEmail === detailsEmail;
                                if (isDetailsForCurrent && candidateDetails?.resumedetails?.full_name) {
                                  return candidateDetails.resumedetails.full_name;
                                }
                                return selectedCandidate.full_name || selectedCandidate.name || selectedCandidate.first_name || "Unknown Candidate";
                              })()}
                            </Typography>
                            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" gap={1} mb={2}>
                              {(() => {
                                const selectedEmail = normalizeEmail(selectedCandidate?.email || selectedCandidate?.email_address);
                                const detailsEmail = normalizeEmail(candidateDetails?.resumedetails?.email_address || candidateDetails?.resumedetails?.email);
                                const isDetailsForCurrent = selectedEmail && detailsEmail && selectedEmail === detailsEmail;
                                const role = isDetailsForCurrent
                                  ? (candidateDetails?.resumedetails?.current_role || candidateDetails?.resumedetails?.alias || candidateDetails?.resumedetails?.application_title)
                                  : (selectedCandidate.role || selectedCandidate.title || selectedCandidate.job_title);

                                return role ? (
                                  <Chip
                                    icon={<Work sx={{ fontSize: 18 }} />}
                                    label={role}
                                    sx={{
                                      bgcolor: "#e3f2fd",
                                      color: "#1976d2",
                                      fontWeight: 600,
                                      fontSize: "0.875rem",
                                      border: "1px solid rgba(25, 118, 210, 0.2)",
                                      boxShadow: "0 2px 8px rgba(25, 118, 210, 0.15)",
                                      height: 36,
                                      px: 1,
                                      "& .MuiChip-icon": {
                                        color: "#1976d2"
                                      },
                                      "&:hover": {
                                        bgcolor: "#bbdefb",
                                        transform: "translateY(-2px)",
                                        boxShadow: "0 4px 12px rgba(25, 118, 210, 0.2)",
                                      },
                                      transition: "all 0.3s ease",
                                    }}
                                    size="medium"
                                  />
                                ) : null;
                              })()}
                              {(() => {
                                const selectedEmail = normalizeEmail(selectedCandidate?.email || selectedCandidate?.email_address);
                                const detailsEmail = normalizeEmail(candidateDetails?.resumedetails?.email_address || candidateDetails?.resumedetails?.email);
                                const isDetailsForCurrent = selectedEmail && detailsEmail && selectedEmail === detailsEmail;
                                const value = isDetailsForCurrent
                                  ? (candidateDetails?.resumedetails?.total_experience)
                                  : (selectedCandidate?.total_experience || selectedCandidate?.experience);
                                if (
                                  value == null ||
                                  value === "" ||
                                  String(value).toLowerCase() === "undefined" ||
                                  String(value).toLowerCase() === "null" ||
                                  String(value).trim() === "0"
                                ) {
                                  return null;
                                }
                                return (
                                  <Chip
                                    icon={<Person sx={{ fontSize: 18 }} />}
                                    label={`Experience: ${value}`}
                                    sx={{
                                      bgcolor: "#e0f7fa",
                                      color: "#00796b",
                                      fontWeight: 600,
                                      fontSize: "0.875rem",
                                      border: "1px solid #b2dfdb",
                                      height: 36,
                                      px: 1,
                                      "& .MuiChip-icon": { color: "#00796b" },
                                    }}
                                    size="medium"
                                  />
                                );
                              })()}
                            </Stack>
                            {/* Contact Info & Location - All on one line, smaller size */}
                            <Stack direction="row" spacing={2.5} alignItems="center" flexWrap="wrap" gap={1.5}>
                              {(selectedCandidate.email || selectedCandidate.email_address) && (
                                <Stack
                                  direction="row"
                                  spacing={0.75}
                                  alignItems="center"
                                  component="a"
                                  href={`mailto:${selectedCandidate.email || selectedCandidate.email_address}`}
                                  sx={{
                                    textDecoration: "none",
                                    cursor: "pointer",
                                    borderRadius: "8px",
                                    px: 1.5,
                                    py: 0.5,
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                      bgcolor: "#f1f5f9",
                                      transform: "translateY(-1px)",
                                    }
                                  }}
                                >
                                  <Email sx={{ color: "#64748b", fontSize: 14 }} />
                                  <Typography variant="body2" sx={{ color: "#475569", fontWeight: 500, fontSize: "0.75rem" }}>
                                    {selectedCandidate.email || selectedCandidate.email_address}
                                  </Typography>
                                  <CheckCircle sx={{ color: "#10b981", fontSize: 14 }} />
                                </Stack>
                              )}
                              {/* Phone and Location grouped together */}
                              <Stack direction="row" spacing={1.5} alignItems="center">
                                {/* {(candidateDetails && candidateDetails.resumedetails && candidateDetails.resumedetails.total_experience &&
                             (selectedCandidate.email || selectedCandidate.email_address) === (candidateDetails.resumedetails.email_address || candidateDetails.resumedetails.email)) && (
                               <Typography variant="subtitle2" sx={{ color: '#1976d2', fontWeight: 700, mb: 1 }}>
                                 Total Experience: {candidateDetails.resumedetails.total_experience}
                               </Typography>
                             )} */}

                                {(() => {
                                  const selectedEmail = normalizeEmail(selectedCandidate?.email || selectedCandidate?.email_address);
                                  const detailsEmail = normalizeEmail(candidateDetails?.resumedetails?.email_address || candidateDetails?.resumedetails?.email);
                                  const isDetailsForCurrent = selectedEmail && detailsEmail && selectedEmail === detailsEmail;
                                  const phone = isDetailsForCurrent
                                    ? (candidateDetails?.resumedetails?.home_phone || candidateDetails?.resumedetails?.phone || candidateDetails?.resumedetails?.phone_number || candidateDetails?.resumedetails?.mobile)
                                    : (selectedCandidate.phone || selectedCandidate.phone_number || selectedCandidate.mobile);

                                  return phone ? (
                                    <Stack
                                      direction="row"
                                      spacing={0.75}
                                      alignItems="center"
                                      component="a"
                                      href={`tel:${phone}`}
                                      sx={{
                                        textDecoration: "none",
                                        cursor: "pointer",
                                        borderRadius: "8px",
                                        px: 1.5,
                                        py: 0.5,
                                        transition: "all 0.2s ease",
                                        "&:hover": {
                                          bgcolor: "#f1f5f9",
                                          transform: "translateY(-1px)",
                                        }
                                      }}
                                    >
                                      <Phone sx={{ color: "#64748b", fontSize: 14 }} />
                                      <Typography variant="body2" sx={{ color: "#475569", fontWeight: 500, fontSize: "0.75rem" }}>
                                        {phone}
                                      </Typography>
                                      <CheckCircle sx={{ color: "#10b981", fontSize: 14 }} />
                                    </Stack>
                                  ) : null;
                                })()}
                                {(() => {
                                  const selectedEmail = normalizeEmail(selectedCandidate?.email || selectedCandidate?.email_address);
                                  const detailsEmail = normalizeEmail(candidateDetails?.resumedetails?.email_address || candidateDetails?.resumedetails?.email);
                                  const isDetailsForCurrent = selectedEmail && detailsEmail && selectedEmail === detailsEmail;

                                  let locationParts = [];
                                  if (isDetailsForCurrent && candidateDetails?.resumedetails) {
                                    const cityname = candidateDetails.resumedetails.cityname;
                                    const statename = candidateDetails.resumedetails.statename;
                                    const countryname = candidateDetails.resumedetails.countryname;
                                    if (cityname) locationParts.push(cityname);
                                    if (statename) locationParts.push(statename);
                                    if (countryname) locationParts.push(countryname);
                                  } else {
                                    locationParts = [selectedCandidate.city, selectedCandidate.state].filter(Boolean);
                                  }

                                  return locationParts.length > 0 ? (
                                    <Stack
                                      direction="row"
                                      spacing={0.75}
                                      alignItems="center"
                                      sx={{
                                        borderRadius: "8px",
                                        px: 1.5,
                                        py: 0.5,
                                      }}
                                    >
                                      <LocationOn sx={{ color: "#64748b", fontSize: 14 }} />
                                      <Typography variant="body2" sx={{ color: "#475569", fontWeight: 500, fontSize: "0.75rem" }}>
                                        {locationParts.join(", ")}
                                      </Typography>
                                    </Stack>
                                  ) : null;
                                })()}
                              </Stack>

                            </Stack>
                          </Box>
                        </Stack>
                        {selectedCandidate.profile && (
                          <Stack direction="row" spacing={1.5}>
                            <Button
                              variant="contained"
                              startIcon={<Description />}
                              size="small"
                              onClick={openResume}
                              sx={{
                                bgcolor: "#667eea",
                                color: "#fff",
                                px: 2,
                                py: 0.75,
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "0.75rem",
                                boxShadow: "0 2px 8px rgba(102, 126, 234, 0.25)",
                                "&:hover": {
                                  bgcolor: "#5568d3",
                                  boxShadow: "0 4px 12px rgba(102, 126, 234, 0.35)",
                                  transform: "translateY(-1px)",
                                },
                                transition: "all 0.3s ease",
                                "& .MuiButton-startIcon": {
                                  marginRight: "6px",
                                  "& > *:nth-of-type(1)": {
                                    fontSize: "16px"
                                  }
                                }
                              }}
                            >
                              View Resume
                            </Button>
                            <Button
                              variant="outlined"
                              startIcon={<Download />}
                              size="small"
                              onClick={async () => {
                                try {
                                  const resumeUrl = `${Key.domain_crm}/common/get_jobfair_file/${selectedCandidate.id}/${selectedCandidate.profile}`;
                                  const response = await fetch(resumeUrl);
                                  const blob = await response.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.download = selectedCandidate.profile || `resume_${selectedCandidate.id}.pdf`;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                  window.URL.revokeObjectURL(url);
                                } catch (error) {
                                  console.error("Error downloading resume:", error);
                                  // Fallback: open in new tab if download fails
                                  const resumeUrl = `${Key.domain_crm}/common/get_jobfair_file/${selectedCandidate.id}/${selectedCandidate.profile}`;
                                  window.open(resumeUrl, '_blank', 'noopener,noreferrer');
                                }
                              }}
                              sx={{
                                bgcolor: "#fff",
                                color: "#667eea",
                                borderColor: "#667eea",
                                px: 2,
                                py: 0.75,
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "0.75rem",
                                "&:hover": {
                                  bgcolor: "#f3f4f6",
                                  borderColor: "#5568d3",
                                  color: "#5568d3",
                                  boxShadow: "0 2px 8px rgba(102, 126, 234, 0.2)",
                                  transform: "translateY(-1px)",
                                },
                                transition: "all 0.3s ease",
                                "& .MuiButton-startIcon": {
                                  marginRight: "6px",
                                  "& > *:nth-of-type(1)": {
                                    fontSize: "16px"
                                  }
                                }
                              }}
                            >
                              Download Resume
                            </Button>
                          </Stack>
                        )}
                      </Stack>
                    </Box>

                    <Box sx={{ flex: 1, overflow: "auto", bgcolor: "#f8f9fa", p: { xs: 1.5, md: 2.5 } }}>
                      <Grid container spacing={{ xs: 1.5, md: 2 }}>





                        {/* Profile Summary Section - Remove static text and use API if available */}
                        <Grid item xs={12} sx={{ mt: 0 }}>
                          <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 2 }}>
                            <Box sx={{
                              width: 5,
                              height: 32,
                              bgcolor: "#10b981",
                              borderRadius: "0 8px 8px 0",
                              boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)"
                            }} />
                            <Box>
                              <Typography variant="h5" sx={{ fontWeight: 800, color: "#1a202c", fontSize: "1.25rem", mb: 0.5, letterSpacing: "-0.3px" }}>
                                Profile Summary
                              </Typography>
                              <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.8rem" }}>
                                Overview of candidate's background
                              </Typography>
                            </Box>
                          </Box>
                          <Card sx={{
                            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                            borderRadius: 4,
                            border: "none",
                            background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
                            position: "relative",
                            overflow: "hidden",
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              height: "4px",
                              background: "linear-gradient(90deg, #10b981 0%, #34d399 100%)",
                            },
                          }}>
                            <CardContent sx={{ p: 2.5 }}>

                              <Typography variant="body1" sx={{ fontWeight: 400, color: "#475569", lineHeight: 1.85, fontSize: "1rem" }}>
                                {isDetailsLoading ? (
                                  <Skeleton variant="text" width={280} height={28} />
                                ) : candidateDetails && (selectedCandidate.email || selectedCandidate.email_address) === (candidateDetails?.resumedetails?.email_address || candidateDetails?.resumedetails?.email)
                                  ? (candidateDetails.resumedetails.summary
                                    ? candidateDetails.resumedetails.summary.replace(/<[^>]+>/g, ' ').replace(/ +/g, ' ').trim()
                                    : "No summary available.")
                                  : "No summary available."}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>

                        {/* Experience Section - Dynamic */}
                        <Grid item xs={12} sx={{ mt: 0 }}>
                          <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1.25 }}>
                            <Box sx={{ width: 5, height: 32, bgcolor: "#f59e0b", borderRadius: "0 8px 8px 0", boxShadow: "0 2px 8px rgba(245, 158, 11, 0.3)" }} />
                            <Box>
                              <Typography variant="h5" sx={{ fontWeight: 800, color: "#1a202c", fontSize: "1.25rem", mb: 0.5, letterSpacing: "-0.3px" }}>
                                Professional Experience
                              </Typography>
                              <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.8rem" }}>
                                Candidate's work history and expertise
                              </Typography>
                            </Box>
                          </Box>
                          <Card sx={{ boxShadow: "0 4px 16px rgba(0,0,0,0.08)", borderRadius: 4, border: "none", background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)", position: "relative", overflow: "hidden", "&::before": { content: '""', position: "absolute", top: 0, left: 0, right: 0, height: "4px", background: "linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%)" } }}>
                            <CardContent sx={{ p: 2.5 }}>
                              {(() => {
                                const selectedEmail = normalizeEmail(selectedCandidate?.email || selectedCandidate?.email_address);
                                const detailsEmail = normalizeEmail(candidateDetails?.resumedetails?.email_address || candidateDetails?.resumedetails?.email);
                                const isDetailsForCurrent = selectedEmail && detailsEmail && selectedEmail === detailsEmail;
                                if (isDetailsLoading) {
                                  return <Skeleton variant="rectangular" width="100%" height={70} />;
                                }
                                if (!isDetailsForCurrent || !Array.isArray(candidateDetails?.projectdetails) || candidateDetails.projectdetails.length === 0) {
                                  return <Typography color="text.secondary">No experience available.</Typography>;
                                }

                                return (
                                  <Box sx={{ position: "relative", pl: { xs: 3, md: 4 } }}>
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        left: { xs: 8, md: 12 },
                                        top: 0,
                                        bottom: 0,
                                        width: "3px",
                                        bgcolor: "linear-gradient(180deg, #f59e0b 0%, #fbbf24 50%, #fcd34d 100%)",
                                        background: "linear-gradient(180deg, #f59e0b 0%, #fbbf24 50%, #fcd34d 100%)",
                                        borderRadius: 2,
                                        boxShadow: "0 0 8px rgba(245, 158, 11, 0.3)",
                                      }}
                                    />
                                    {candidateDetails.projectdetails.map((project, idx) => {
                                      const start = project.pro_startdate && project.pro_startdate !== 'Invalid date' ? project.pro_startdate : '';
                                      const end = project.pro_till_date === 'Present' ? 'Present' : (project.pro_enddate && project.pro_enddate !== 'Invalid date' ? project.pro_enddate : '');
                                      const dateRange = start || end ? `${start}${start && end ? ' - ' : ''}${end}` : '';
                                      const roleTitle = project.pro_role || project.role;
                                      const company = project.pro_client ? ` @ ${project.pro_client}` : '';
                                      const responsibilities = project.pro_responsibilities;
                                      const description = project.pro_description;
                                      return (
                                        <Box key={project.id || idx} sx={{ position: "relative", mb: 4 }}>
                                          <Box
                                            sx={{
                                              position: "absolute",
                                              left: { xs: -20, md: -28 },
                                              top: 4,
                                              width: { xs: 18, md: 22 },
                                              height: { xs: 18, md: 22 },
                                              borderRadius: "50%",
                                              bgcolor: "#f59e0b",
                                              border: "4px solid #fff",
                                              boxShadow: "0 4px 12px rgba(245, 158, 11, 0.5), 0 0 0 4px rgba(245, 158, 11, 0.2)",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              zIndex: 2,
                                            }}
                                          />
                                          <Box sx={{ ml: { xs: 2, md: 3 }, bgcolor: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)", borderRadius: "16px", p: 2.5, border: "1px solid rgba(245, 158, 11, 0.25)" }}>
                                            <Stack direction="row" spacing={1.5} alignItems="center" mb={0.5}>
                                              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1a202c" }}>
                                                {roleTitle}{company}
                                              </Typography>
                                              {end === 'Present' && (
                                                <Chip label="Current" size="small" sx={{ bgcolor: "#f59e0b", color: "#fff", fontWeight: 600, height: 20 }} />
                                              )}
                                            </Stack>
                                            {dateRange && (
                                              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                                                {dateRange}
                                              </Typography>
                                            )}
                                            {responsibilities ? (
                                              <Typography variant="body2" sx={{ color: "#475569", mt: 1 }}>{responsibilities}</Typography>
                                            ) : description ? (
                                              <Typography variant="body2" sx={{ color: "#475569", mt: 1 }} dangerouslySetInnerHTML={{ __html: description }} />
                                            ) : null}
                                          </Box>
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                );
                              })()}
                            </CardContent>
                          </Card>
                        </Grid>

                        {/* Skills Section - Dynamic */}
                        <Grid item xs={12} sx={{ mt: 0 }}>
                          <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1.25 }}>
                            <Box sx={{ width: 5, height: 32, bgcolor: "#8b5cf6", borderRadius: "0 8px 8px 0", boxShadow: "0 2px 8px rgba(139, 92, 246, 0.3)" }} />
                            <Box>
                              <Typography variant="h5" sx={{ fontWeight: 800, color: "#1a202c", fontSize: "1.25rem", mb: 0.5, letterSpacing: "-0.3px" }}>
                                Technical Skills
                              </Typography>
                              <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.8rem" }}>
                                Key competencies and technologies
                              </Typography>
                            </Box>
                          </Box>
                          <Card sx={{ boxShadow: "0 4px 16px rgba(0,0,0,0.08)", borderRadius: 4, border: "none", background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)", position: "relative", overflow: "hidden", "&::before": { content: '""', position: "absolute", top: 0, left: 0, right: 0, height: "4px", background: "linear-gradient(90deg, #ffc107 0%, #ffc107 100%)" } }}>
                            <CardContent sx={{ p: 2.5 }}>
                              {(() => {
                                const selectedEmail = normalizeEmail(selectedCandidate?.email || selectedCandidate?.email_address);
                                const detailsEmail = normalizeEmail(candidateDetails?.resumedetails?.email_address || candidateDetails?.resumedetails?.email);
                                const isDetailsForCurrent = selectedEmail && detailsEmail && selectedEmail === detailsEmail;
                                if (isDetailsLoading) {
                                  return <Skeleton variant="rectangular" width="100%" height={36} />;
                                }
                                if (!isDetailsForCurrent) {
                                  return <Typography color="text.secondary">No skills available.</Typography>;
                                }
                                const ai = candidateDetails?.resumedetails?.ai_skills;
                                const skillNames = candidateDetails?.resumedetails?.skillNames;
                                const skills = candidateDetails?.resumedetails?.skills;
                                const list = ai || skillNames || skills;
                                if (!list) return <Typography color="text.secondary">No skills available.</Typography>;
                                const skillsArray = list.split(',').map(s => s.trim()).filter(Boolean);
                                const visibleSkills = showAllSkills ? skillsArray : skillsArray.slice(0, 10);
                                return (
                                  <>
                                    <Stack direction="row" spacing={1.5} flexWrap="wrap" gap={1.5}>
                                      {visibleSkills.map((s, i) => (
                                        <Chip key={`${s}-${i}`} label={s} size="medium" sx={{ bgcolor: "#76727e", color: "#fff", fontWeight: 500 }} />
                                      ))}
                                    </Stack>
                                    {skillsArray.length > 10 && (
                                      <Box sx={{ mt: 2 }}>
                                        <Button size="small" variant="text" onClick={() => setShowAllSkills(!showAllSkills)}>
                                          {showAllSkills ? "Show less" : `Show more (+${skillsArray.length - 10})`}
                                        </Button>
                                      </Box>
                                    )}
                                  </>
                                );
                              })()}
                            </CardContent>
                          </Card>
                        </Grid>

                        {/* Education Section */}
                        <Grid item xs={12} sx={{ mt: 0 }}>
                          <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1.25 }}>
                            <Box sx={{ width: 5, height: 32, bgcolor: "#0ea5e9", borderRadius: "0 8px 8px 0", boxShadow: "0 2px 8px rgba(14,165,233,0.3)" }} />
                            <Box>
                              <Typography variant="h5" sx={{ fontWeight: 800, color: "#1a202c", fontSize: "1.25rem", mb: 0.5, letterSpacing: "-0.3px" }}>
                                Education
                              </Typography>
                              <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.8rem" }}>
                                Degrees and qualifications
                              </Typography>
                            </Box>
                          </Box>
                          <Card sx={{ boxShadow: "0 4px 16px rgba(0,0,0,0.08)", borderRadius: 4, border: "none", background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)", position: "relative", overflow: "hidden", "&::before": { content: '""', position: "absolute", top: 0, left: 0, right: 0, height: "4px", background: "linear-gradient(90deg, #0ea5e9 0%, #38bdf8 100%)" } }}>
                            <CardContent sx={{ p: 2.5 }}>
                              {(() => {
                                const selectedEmail = normalizeEmail(selectedCandidate?.email || selectedCandidate?.email_address);
                                const detailsEmail = normalizeEmail(candidateDetails?.resumedetails?.email_address || candidateDetails?.resumedetails?.email);
                                const isDetailsForCurrent = selectedEmail && detailsEmail && selectedEmail === detailsEmail;
                                if (isDetailsLoading) {
                                  return <Skeleton variant="rectangular" width="100%" height={60} />;
                                }
                                if (!isDetailsForCurrent || !Array.isArray(candidateDetails?.educationdetails) || candidateDetails.educationdetails.length === 0) {
                                  return <Typography color="text.secondary">No data available.</Typography>;
                                }
                                return (
                                  <Stack direction="column" spacing={1.5}>
                                    {candidateDetails.educationdetails.map((ed) => (
                                      <Box key={ed.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", border: "1px solid rgba(14,165,233,0.2)", borderRadius: 2, p: 1.5, bgcolor: "#fff" }}>
                                        <Typography variant="body1" sx={{ color: "#1f2937", fontWeight: 600 }}>
                                          {ed.edu_course || "Education"}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: "#64748b" }}>
                                          {ed.edu_university || ""} {ed.edu_passing_year ? `• ${ed.edu_passing_year}` : ""}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Stack>
                                );
                              })()}
                            </CardContent>
                          </Card>
                        </Grid>

                        {/* Certificates Section */}
                        <Grid item xs={12} sx={{ mt: 0 }}>
                          <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1.25 }}>
                            <Box sx={{ width: 5, height: 32, bgcolor: "#10b981", borderRadius: "0 8px 8px 0", boxShadow: "0 2px 8px rgba(16,185,129,0.3)" }} />
                            <Box>
                              <Typography variant="h5" sx={{ fontWeight: 800, color: "#1a202c", fontSize: "1.25rem", mb: 0.5, letterSpacing: "-0.3px" }}>
                                Certificates
                              </Typography>
                              <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.8rem" }}>
                                Licenses and certifications
                              </Typography>
                            </Box>
                          </Box>
                          <Card sx={{ boxShadow: "0 4px 16px rgba(0,0,0,0.08)", borderRadius: 4, border: "none", background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)", position: "relative", overflow: "hidden", "&::before": { content: '""', position: "absolute", top: 0, left: 0, right: 0, height: "4px", background: "linear-gradient(90deg, #10b981 0%, #34d399 100%)" } }}>
                            <CardContent sx={{ p: 2.5 }}>
                              {(() => {
                                const selectedEmail = normalizeEmail(selectedCandidate?.email || selectedCandidate?.email_address);
                                const detailsEmail = normalizeEmail(candidateDetails?.resumedetails?.email_address || candidateDetails?.resumedetails?.email);
                                const isDetailsForCurrent = selectedEmail && detailsEmail && selectedEmail === detailsEmail;
                                if (isDetailsLoading) {
                                  return <Skeleton variant="rectangular" width="100%" height={60} />;
                                }
                                if (!isDetailsForCurrent || !Array.isArray(candidateDetails?.certificatedetails) || candidateDetails.certificatedetails.length === 0) {
                                  return <Typography color="text.secondary">No data available.</Typography>;
                                }
                                return (
                                  <Stack direction="column" spacing={1.5}>
                                    {candidateDetails.certificatedetails.map((cert) => (
                                      <Box key={cert.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", border: "1px solid rgba(16,185,129,0.2)", borderRadius: 2, p: 1.5, bgcolor: "#fff" }}>
                                        <Typography variant="body1" sx={{ color: "#1f2937", fontWeight: 600 }}>
                                          {cert.cer_name || "Certificate"}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: "#64748b" }}>
                                          {cert.cer_year || ""}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Stack>
                                );
                              })()}
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    textAlign: "center",
                  }}
                >
                  <EventNoteOutlined sx={{ fontSize: 64, color: "#ccc", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Select a candidate to view details
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default JobFairCandidates;

